import {memo} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {headingXS, body14Normal, body16Normal} from '~/shared/theme/typography';
import {media} from '~/shared/theme/media';
import PieButton from '~/shared/theme/pie/PieButton';

const Root = styled.div`
  ${flexColumn}
  padding: 0 16px 32px;

  &&& {
    width: 100%;
    background-color: ${({theme}) => theme.colors.surface};
  }

  ${media.minLargeMobile`
    padding: 24px 32px;
    &&& {
      width: 500px;
    }
  `}
`;

const Title = styled.h2`
  ${headingXS}
  margin: 0;
  color: ${({theme}) => theme.colors.charcoal80};
`;

const SubTitle = styled.p`
  ${body14Normal}
  line-height: 20px;
  margin: 8px 0 0;
  color: ${({theme}) => theme.colors.text};
`;

const List = styled.ul`
  ${flexColumn}
  gap: 8px;
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
  max-height: 200px;
`;

const InvoiceLink = styled.a`
  ${body16Normal}
  display: block;
  padding: 4px;
  color: ${({theme}) => theme.colors.surfacePrimaryAction};
  text-decoration: none;

  &:hover,
  &:focus {
    background-color: ${({theme}) => theme.colors.lightBackground};
  }
`;

const CloseButton = styled(PieButton)`
  width: 100%;
  margin-top: 32px;
`;

type DealInvoicesModalProps = {
  args: {
    inoviceUrls: string[];
  };
  closeModal: () => void;
};

const DealInvoicesModal = ({args: {inoviceUrls}, closeModal}: DealInvoicesModalProps) => {
  const {t} = getLocalizationService();

  return (
    <Root>
      <Title id="modal-title">{t('download_deal_invoice')}</Title>
      <SubTitle>{t('deal_invoices_modal_subtitle')}</SubTitle>
      <List data-test-id="deal-invoices-list">
        {inoviceUrls.map((url, index) => (
          <li key={url}>
            {/* A real anchor, so every invoice is opened by its own click and never hits the popup blocker */}
            <InvoiceLink
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-test-id={`deal-invoice-link-${index}`}
            >
              {t('deal_invoice_number', {number: index + 1})}
            </InvoiceLink>
          </li>
        ))}
      </List>
      <CloseButton variant="primaryLarge" onClick={closeModal} data-test-id="deal-invoices-close">
        {t('close')}
      </CloseButton>
    </Root>
  );
};

type StaticTypes = {
  mobileBottomSheet?: boolean;
};

const EnhancedDealInvoicesModal: React.FC<DealInvoicesModalProps> & StaticTypes = memo(DealInvoicesModal);

EnhancedDealInvoicesModal.mobileBottomSheet = true;

export default attachModal('dealInvoicesModal', EnhancedDealInvoicesModal);
